import { defer, MonoTypeOperatorFunction, tap } from 'rxjs';
import { SERVER_DEBUG_LOG_ENABLED } from './feature-flags';
import { logServerError, logServerSuccess, nextServerLogId } from './server-log';

/** Logs JSON responses without consuming or changing the response returned to the caller. */
export async function fetchWithBackendLog(input: string, init?: RequestInit): Promise<Response> {
  if (!SERVER_DEBUG_LOG_ENABLED) return fetch(input, init);
  const id = nextServerLogId();
  const startedAt = Date.now();
  const method = `${init?.method ?? 'GET'} ${input.split('?')[0]}`;
  try {
    const response = await fetch(input, init);
    // Read a clone so the application's response.json() remains untouched.
    try {
      const body: unknown = await response.clone().json();
      const options = { id, durationMs: Date.now() - startedAt, transport: { status: response.status } };
      if (response.ok) logServerSuccess('BACKEND HTTP', method, body, options);
      else logServerError('BACKEND HTTP', method, body, options);
    } catch {
      const write = response.ok ? logServerSuccess : logServerError;
      write('BACKEND HTTP', method, { body: 'Resposta sem JSON' }, {
        id, durationMs: Date.now() - startedAt, transport: { status: response.status },
      });
    }
    return response;
  } catch (error: unknown) {
    logServerError('BACKEND HTTP', method, error, { id, durationMs: Date.now() - startedAt });
    throw error;
  }
}

/** Axios Observable responses are captured before DTO mapping or catchError fallbacks. */
export function logHttpResponse<T extends { data: unknown; status: number }>(method: string): MonoTypeOperatorFunction<T> {
  return source => defer(() => {
    const startedAt = Date.now();
    const id = nextServerLogId();
    return source.pipe(tap({
      next: response => logServerSuccess('BACKEND HTTP', method, response.data, {
        id, durationMs: Date.now() - startedAt, transport: { status: response.status },
      }),
      error: (error: unknown) => logServerError('BACKEND HTTP', method, error, {
        id, durationMs: Date.now() - startedAt,
      }),
    }));
  });
}
